import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CriterionActionButtons } from './CriterionActionButtons';
import { CriterionList } from './CriterionList';
import { SimpleOperatorList } from './SimpleOperatorList';
import { CompoundOperatorList } from './CompoundOperatorList';
import { EditEntityDialog } from './EditEntityDialog';
import { CombineCriteriaDialog } from './CombineCriteriaDialog';
import { cloneEntitySelectionCriteria } from '../helpers/constructors';
import { v4 as uuid } from 'uuid';
import {
  QueryableFieldsQuery,
  useQueryableFieldsQuery,
} from '../../../../types/generated-types';
import { Box } from '@mui/material';
import {
  EntityCriteriaCompoundOperatorInput,
  EntityCriteriaSimpleOperatorInput,
  EntitySelectionCriteriaInput,
  TieredEntitySelectionCriterionInput,
} from '../../../../types/generated-types';
import BorderedSection from '../../../features/shared/borderedSection';

export interface EntitySelectionCriteriaBuilderProps {
  criteria: EntitySelectionCriteriaInput;
  onChange: (criteria: EntitySelectionCriteriaInput) => void;
  targetModel: string;
  simplified?: boolean;
}

export const EntitySelectionCriteriaBuilder: React.FC<
  EntitySelectionCriteriaBuilderProps
> = ({
  criteria,
  onChange,
  targetModel,
  simplified = false,
}: EntitySelectionCriteriaBuilderProps) => {
  // Dialog control
  const [dialogOpen, setDialogOpen] = useState(false);
  const [combineDialogOpen, setCombineDialogOpen] = useState(false);

  const [_value, _setValue] = useState<EntitySelectionCriteriaInput>(
    cloneEntitySelectionCriteria(criteria),
  );

  const setValue = (value: EntitySelectionCriteriaInput) => {
    _setValue(value);
    onChange(value);
  };

  useEffect(() => {
    _setValue(cloneEntitySelectionCriteria(criteria));
  }, [criteria]);

  const [fieldsData, setFieldsData] = useState<
    QueryableFieldsQuery['queryableFields'] | null
  >(null);

  const fieldsQueryVariables = useMemo(
    () => ({
      modelName: {
        single: targetModel ?? '',
      },
    }),
    [targetModel],
  );

  const {
    data: rawFieldsData,
    loading: fieldsDataLoading,
    error: fieldsDataError,
  } = useQueryableFieldsQuery({
    variables: fieldsQueryVariables,
    skip: !targetModel,
  });

  useEffect(() => {
    setFieldsData(rawFieldsData?.queryableFields ?? null);
  }, [rawFieldsData]);

  // Selected criteria/operators
  const [selectedCriteria, setSelectedCriteria] = useState<Set<string>>(
    new Set(),
  );
  const [selectedOperators, setSelectedOperators] = useState<Set<string>>(
    new Set(),
  );
  const [selectedCompoundOperators, setSelectedCompoundOperators] = useState<
    Set<string>
  >(new Set());

  // Editing criterion/entity states
  const [editingCriterionId, setEditingCriterionId] = useState<string | null>(
    null,
  );
  const [editingSimpleOperatorId, setEditingSimpleOperatorId] = useState<
    string | null
  >(null);
  const [editingCompoundOperatorId, setEditingCompoundOperatorId] = useState<
    string | null
  >(null);

  // Handle add criterion
  const handleAddCriterion = useCallback(() => {
    setEditingCriterionId(null);
    setEditingSimpleOperatorId(null);
    setEditingCompoundOperatorId(null);
    handleOpenDialog();
  }, []);

  // Handle edit criterion
  const handleEditCriterion = (
    criterion: TieredEntitySelectionCriterionInput,
  ) => {
    setEditingCriterionId(criterion.id);
    setEditingSimpleOperatorId(null);
    setEditingCompoundOperatorId(null);

    handleOpenDialog();
  };

  // Handle edit simple entity
  const handleEditSimpleOperator = (
    entity: EntityCriteriaSimpleOperatorInput,
  ) => {
    setEditingCriterionId(null);
    setEditingSimpleOperatorId(entity.id);
    setEditingCompoundOperatorId(null);

    handleOpenDialog();
  };

  // Handle edit compound entity
  const handleEditCompoundOperator = (
    entity: EntityCriteriaCompoundOperatorInput,
  ) => {
    setEditingCriterionId(null);
    setEditingSimpleOperatorId(null);
    setEditingCompoundOperatorId(entity.id);

    handleOpenDialog();
  };

  const handleCriterionSelect = (criterionId: string) => {
    const newSelected = new Set(selectedCriteria);
    if (newSelected.has(criterionId)) {
      newSelected.delete(criterionId);
    } else {
      newSelected.add(criterionId);
    }
    setSelectedCriteria(newSelected);
  };

  const handleOperatorSelect = (operatorId: string) => {
    const newSelected = new Set(selectedOperators);
    if (newSelected.has(operatorId)) {
      newSelected.delete(operatorId);
    } else {
      newSelected.add(operatorId);
    }
    setSelectedOperators(newSelected);
  };

  const handleCompoundOperatorSelect = (operatorId: string) => {
    const newSelected = new Set(selectedCompoundOperators);
    if (newSelected.has(operatorId)) {
      newSelected.delete(operatorId);
    } else {
      newSelected.add(operatorId);
    }
    setSelectedCompoundOperators(newSelected);
  };

  const handleRemoveSelected = useCallback(() => {
    const newCriteria = criteria.criteria.filter(
      (criterion) => criterion && !selectedCriteria.has(criterion.id),
    );
    const newSimpleOperators = criteria.simpleOperators?.filter(
      (op) => op && !selectedOperators.has(op.id),
    );
    const newCompoundOperators = criteria.compoundOperators?.filter(
      (op) => op && !selectedCompoundOperators.has(op.id),
    );

    onChange({
      criteria: newCriteria,
      simpleOperators: newSimpleOperators,
      compoundOperators: newCompoundOperators,
    });

    setSelectedCriteria(new Set());
    setSelectedOperators(new Set());
    setSelectedCompoundOperators(new Set());
  }, [
    criteria,
    onChange,
    selectedCriteria,
    selectedOperators,
    selectedCompoundOperators,
  ]);

  const handleInvertSelected = useCallback(() => {
    const newCriteria = criteria.criteria.map((criterion) =>
      criterion && selectedCriteria.has(criterion.id)
        ? { ...criterion, invert: !criterion.invert }
        : criterion,
    );
    const newSimpleOperators = criteria.simpleOperators?.map((op) =>
      op && selectedOperators.has(op.id) ? { ...op, invert: !op.invert } : op,
    );
    const newCompoundOperators = criteria.compoundOperators?.map((op) =>
      op && selectedCompoundOperators.has(op.id)
        ? { ...op, invert: !op.invert }
        : op,
    );

    onChange({
      criteria: newCriteria,
      simpleOperators: newSimpleOperators,
      compoundOperators: newCompoundOperators,
    });
  }, [
    criteria,
    onChange,
    selectedCriteria,
    selectedOperators,
    selectedCompoundOperators,
  ]);

  const handleCombine = () => {
    setCombineDialogOpen(true);
  };

  const handleCombineConfirm = (
    operator: EntityCriteriaCompoundOperatorInput,
  ) => {
    const selectedCriteriaItems =
      _value.criteria?.filter(
        (criterion) => criterion && selectedCriteria.has(criterion.id),
      ) ?? [];
    const selectedOperatorItems =
      _value.simpleOperators?.filter(
        (op) => op && selectedOperators.has(op.id),
      ) ?? [];
    const selectedCompoundOperatorItems =
      _value.compoundOperators?.filter(
        (operator) => operator && selectedCompoundOperators.has(operator.id),
      ) ?? [];

    const newCompoundOperator: EntityCriteriaCompoundOperatorInput = {
      id: uuid(),
      operator: operator.operator,
      criteria: {
        criteria: selectedCriteriaItems,
        simpleOperators: selectedOperatorItems,
        compoundOperators: selectedCompoundOperatorItems,
      },
      invert: false,
    };

    const newCriteria =
      _value.criteria.filter(
        (criterion) => criterion && !selectedCriteria.has(criterion.id),
      ) ?? [];
    const newSimpleOperators =
      _value.simpleOperators?.filter(
        (op) => op && !selectedOperators.has(op.id),
      ) ?? [];
    const newCompoundOperators =
      _value.compoundOperators?.filter(
        (op) => op && !selectedCompoundOperators.has(op.id),
      ) ?? [];

    newCompoundOperators.push(newCompoundOperator);

    setValue({
      criteria: newCriteria,
      simpleOperators: newSimpleOperators,
      compoundOperators: newCompoundOperators,
    });

    setSelectedCriteria(new Set());
    setSelectedOperators(new Set());
    setSelectedCompoundOperators(new Set());
    setCombineDialogOpen(false);
  };

  const handleCancel = () => {
    setEditingCriterionId(null);
    setEditingSimpleOperatorId(null);
    setEditingCompoundOperatorId(null);
    handleCloseDialog();
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return fieldsDataLoading ? (
    <div>Loading field information for {targetModel}</div>
  ) : fieldsDataError ? (
    <div>Unable to load field information for {targetModel}</div>
  ) : (
    <BorderedSection
      title={
        simplified ? 'Entity Attribute Settings' : 'Entity Attribute Filters'
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ mb: 2, flex: '0 0 auto' }}>
          <CriterionActionButtons
            selectedCriteria={selectedCriteria}
            selectedOperators={selectedOperators}
            selectedCompoundOperators={selectedCompoundOperators}
            onAddCriterion={handleAddCriterion}
            onInvertSelected={handleInvertSelected}
            onCombineClick={handleCombine}
            onRemoveSelected={handleRemoveSelected}
            simplified={simplified}
          />
          {criteria.criteria && criteria.criteria.length > 0 && (
            <Box sx={{ mb: 2, flex: '0 0 auto' }}>
              <BorderedSection
                title="Criteria"
                // style={{ padding: '2px', margin: '2px' }}
                // ccStyle={{
                //   maxHeight: '30vh',
                //   overflow: 'auto',
                //   padding: '2px',
                //   margin: '-10px 6px 2px 6px',
                // }}
              >
                <CriterionList
                  criteria={criteria.criteria}
                  selectedCriteria={selectedCriteria}
                  fieldsData={fieldsData}
                  onCriterionSelect={handleCriterionSelect}
                  onCriterionEdit={handleEditCriterion}
                />
              </BorderedSection>
            </Box>
          )}
        </Box>

        {!simplified &&
          criteria.simpleOperators &&
          criteria.simpleOperators.length > 0 && (
            <BorderedSection
              title={'Simple Operators'}
              // style={{ padding: '2px', margin: '2px' }}
              // ccStyle={{
              //   maxHeight: '30vh',
              //   overflow: 'auto',
              //   padding: '2px',
              //   margin: '-10px 6px 2px 6px',
              // }}
            >
              <SimpleOperatorList
                operators={criteria.simpleOperators}
                selectedOperators={selectedOperators}
                fieldsData={fieldsData}
                onOperatorSelect={handleOperatorSelect}
                onOperatorEdit={handleEditSimpleOperator}
              />
            </BorderedSection>
          )}

        {!simplified &&
          criteria.compoundOperators &&
          criteria.compoundOperators.length > 0 && (
            <BorderedSection
              title="Compound Operators"
              // style={{ padding: '2px', margin: '2px' }}
              // ccStyle={{
              //   maxHeight: '30vh',
              //   overflow: 'auto',
              //   padding: '2px',
              //   margin: '-10px 6px 2px 6px',
              // }}
            >
              <CompoundOperatorList
                operators={criteria.compoundOperators}
                selectedOperators={selectedCompoundOperators}
                fieldsData={fieldsData}
                targetModel={targetModel}
                onOperatorSelect={handleCompoundOperatorSelect}
                onOperatorEdit={handleEditCompoundOperator}
              />
            </BorderedSection>
          )}
      </Box>

      {/* Dialog for add/edit criterion */}
      <EditEntityDialog
        dialogOpen={dialogOpen}
        editingCriterionId={editingCriterionId}
        editingOperatorId={editingSimpleOperatorId}
        editingCompoundOperatorId={editingCompoundOperatorId}
        targetModel={targetModel}
        fieldsData={fieldsData}
        entitySelectionSpec={criteria}
        onSave={onChange}
        onCancel={handleCancel}
        onError={(message) => console.error(message)}
        simplified={simplified}
      />

      {/* Dialog for combining criteria */}
      <CombineCriteriaDialog
        open={combineDialogOpen}
        onClose={() => setCombineDialogOpen(false)}
        selectedCriteria={{
          criteria: criteria.criteria.filter((c) => selectedCriteria.has(c.id)),
          simpleOperators: (criteria.simpleOperators ?? []).filter((o) =>
            selectedOperators.has(o.id),
          ),
          compoundOperators: (criteria.compoundOperators ?? []).filter((o) =>
            selectedCompoundOperators.has(o.id),
          ),
        }}
        targetModel={targetModel}
        fieldsData={fieldsData}
        onCombine={handleCombineConfirm}
      />
    </BorderedSection>
  );
};
